<script setup>
import { defineProps, defineEmits, onMounted, onBeforeUnmount, nextTick } from 'vue';

defineProps({
    tabs: {
        type: Array,
        required: true,
    },
    activeTab: {
        type: String,
        required: true
    },
    disabledTabs: {
        type: Object,
        default: () => ({}),
    },
    itemsCount: {
        type: Object,
        default: () => ({}),
    }
});

const emit = defineEmits(['update:activeTab']);

const updateTabIndicator = () => {
    nextTick(() => {
        const activeTabElement = document.querySelector('.creator-profile__tab-btn--active')
        const tabsContainer = document.querySelector('.creator-profile__tabs')
        
        if (activeTabElement && tabsContainer) {
            const tabLeft = activeTabElement.offsetLeft
            const tabWidth = activeTabElement.offsetWidth
            
            tabsContainer.style.setProperty('--tab-width', `${tabWidth}px`)
            tabsContainer.style.setProperty('--tab-left', `${tabLeft}px`)
            
            // Force a visual indicator for debugging
            tabsContainer.style.setProperty('--debug-bg', 'red')
        }
    })
}

const handleTabChange = (tabId, isDisabled) => {
    if (isDisabled) return;
    
    emit('update:activeTab', tabId);
    // Give it a longer delay to ensure DOM is fully updated
    setTimeout(() => {
        updateTabIndicator();
    }, 50);
};

onMounted(() => {
    // Give DOM time to render before calculating positions
    setTimeout(() => {
        updateTabIndicator()
    }, 100)
    window.addEventListener('resize', updateTabIndicator)
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', updateTabIndicator)
})
</script>

<template>
    <nav class="creator-profile__tabs">
        <button v-for="tab in tabs" :key="tab.id" class="creator-profile__tab-btn"
            :class="{ 
                'creator-profile__tab-btn--active': tab.id === activeTab,
                'creator-profile__tab-btn--disabled': disabledTabs[tab.id]
            }" 
            @click="handleTabChange(tab.id, disabledTabs[tab.id])"
            @mousedown="!disabledTabs[tab.id] && $event.currentTarget.classList.add('tab-clicking')"
            @mouseup="$event.currentTarget.classList.remove('tab-clicking')"
            @mouseleave="$event.currentTarget.classList.remove('tab-clicking')"
            :title="disabledTabs[tab.id] || ''"
            :data-testid="'tab_'+ tab.id">
            {{ tab.label }}
           
            <span v-if="tab.id === 'all' && itemsCount[tab.id] && itemsCount[tab.id] > 0" 
                  class="ml-2 px-2 py-0.5 text-xs bg-echoes-primary text-white rounded-full font-medium">
                {{ itemsCount[tab.id] }}
            </span>
            <span v-if="disabledTabs[tab.id]" class="creator-profile__tab-tooltip">{{ disabledTabs[tab.id] }}</span>
        </button>
    </nav>
</template>
<style scoped lang="postcss">
.creator-profile__tabs {
    @apply relative flex flex-row border-0 gap-1 xs:gap-6 mx-0 px-0 border-b border-echoes-border-gray w-full overflow-x-auto overflow-y-hidden;

    &::-webkit-scrollbar {
        height: 2px;
        background-color: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background-color: rgba(255, 255, 255, 0.05);
        border-radius: 3px;
    }

    &::-webkit-scrollbar-track {
        background-color: transparent;
    }

    /* Firefox */
    scrollbar-width: none;
    scrollbar-color: transparent transparent;
}

.creator-profile__tab-btn {
    @apply text-sm 2xl:text-base px-2 xs:px-4 pb-2 xs:pb-3 text-white/80 transition-colors flex items-center gap-2 relative font-medium whitespace-nowrap;
}

.creator-profile__tab-btn:hover {
    @apply text-gray-700 dark:text-white;
}

.creator-profile__tab-btn:active {
    transform: translateY(1px);
}

.creator-profile__tab-btn--active {
    @apply text-violet-600 dark:text-white font-medium;
}

.creator-profile__tab-btn--disabled {
    @apply opacity-60 cursor-not-allowed;
}

.creator-profile__tab-btn--disabled:hover {
    @apply text-gray-500 dark:text-gray-400;
}

/* Use ::before for desktop horizontal bar to avoid conflict with mobile ::after */
.creator-profile__tab-btn--active::before {
    content: '';
    @apply absolute block;
    height: 3px;
    width: 100%;
    left: 0;
    bottom: -1px; /* Align perfectly with the tab border */
    z-index: 10;
    overflow: hidden;
    
    /* Layered background with traveling highlight effect */
    background: 
        /* Traveling highlight layer */
        linear-gradient(90deg, 
            transparent 0%,
            transparent 40%,
            rgba(255, 255, 255, 0.9) 45%,
            rgba(248, 250, 252, 1) 50%,
            rgba(255, 255, 255, 0.9) 55%,
            transparent 60%,
            transparent 100%
        ),
        /* Base metallic gradient */
        linear-gradient(90deg, 
            #ffffff 0%,
            #f8fafc 15%,
            #e2e8f0 30%,
            #cbd5e1 45%,
            #94a3b8 60%,
            #cbd5e1 75%,
            #e2e8f0 85%,
            #f8fafc 95%,
            #ffffff 100%
        );
    
    /* Animate the traveling highlight */
    background-size: 300% 100%, 100% 100%;
    background-position: -100% 0, 0 0;
    
    /* Metallic glow effect */
    box-shadow: 
        0 0 2px rgba(248, 250, 252, 0.3),
        0 0 4px rgba(226, 232, 240, 0.2),
        0 0 6px rgba(203, 213, 225, 0.15),
        0 1px 2px rgba(148, 163, 184, 0.1);
    
    /* Combined animations */
    animation: revealLine 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards,
               travelHighlight 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
    transform-origin: left;
}

/* Animated drawing effect from left to right */
@keyframes revealLine {
    0% {
        transform: scaleX(0);
        opacity: 0;
    }
    20% {
        opacity: 1;
    }
    100% {
        transform: scaleX(1);
        opacity: 1;
    }
}

/* Traveling highlight background animation */
@keyframes travelHighlight {
    0% {
        background-position: -100% 0, 0 0;
    }
    100% {
        background-position: 100% 0, 0 0;
    }
}

.creator-profile__tabs::after::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, 
        transparent 0%,
        rgba(255, 255, 255, 0.3) 50%,
        transparent 100%
    );
    animation: shimmer 2s infinite;
}

@keyframes shimmer {
    0% {
        left: -100%;
    }
    100% {
        left: 100%;
    }
}



.creator-profile__tab-tooltip {
    @apply hidden absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-2 py-1 text-xs text-white bg-gray-800 rounded pointer-events-none whitespace-nowrap z-10;
    width: max-content;
    max-width: 200px;
}

.creator-profile__tab-tooltip::before {
    content: '';
    @apply absolute -top-1 left-1/2 transform -translate-x-1/2 border-4 border-transparent border-b-gray-800;
}

.creator-profile__tab-btn--disabled:hover .creator-profile__tab-tooltip {
    @apply block;
}
</style>